import { euro, type WebInvoiceLine } from "../../lib/invoices";

/**
 * Eine Positionszeile, flach gelesen — der Normalzustand einer Zeile.
 *
 * Die Karten mit Eingabefeldern (`InvoiceLineRow`) waren immer im
 * Bearbeiten-Modus, auch wenn man nur die Liste überflog: acht Rahmen und
 * Eingabefelder pro Position machten aus einem Angebot mit fünfzehn Zeilen
 * eine Wand (Benjamin, 01.09.2026, nach Vorlage aus Claude Design). Diese
 * Ansicht zeigt nur Text — dieselbe Tabelle wie im PDF — und tritt auf Klick
 * zur Seite für `InvoiceLineRow`, das eigentliche Eingabeformular.
 */

export type LineRowFlatProps = {
  line: WebInvoiceLine;
  nummer: number;
  gewaehlt: boolean;
  onToggleWahl: () => void;
  onBearbeiten: () => void;
  onDuplicate: () => void;
  onSaveToCatalog: () => void;
  onToggleOptional: () => void;
  onRemove: () => void;
  katalogLaeuft: boolean;
  ziehen: {
    onDragStart: (event: React.DragEvent) => void;
    onDragEnd: () => void;
    ziehtGerade: boolean;
  };
};

export default function InvoiceLineRowFlat({
  line, nummer, gewaehlt, onToggleWahl, onBearbeiten,
  onDuplicate, onSaveToCatalog, onToggleOptional, onRemove, katalogLaeuft, ziehen,
}: LineRowFlatProps) {
  const optional = line.line_kind === "optional";
  const netto = (Number(line.quantity) || 0) * (Number(line.unit_price_net) || 0);

  return (
    <div
      className={"invoice-zeile-flach"
        + (optional ? " optional" : "")
        + (gewaehlt ? " gewaehlt" : "")
        + (ziehen.ziehtGerade ? " is-zieht" : "")}
      draggable
      onDragStart={ziehen.onDragStart}
      onDragEnd={ziehen.onDragEnd}
    >
      <label className="invoice-wahl" onClick={(event) => event.stopPropagation()}>
        <input
          type="checkbox"
          checked={gewaehlt}
          onChange={onToggleWahl}
          aria-label={`„${line.title || "Position"}" auswählen`}
        />
      </label>
      <span className="invoice-zeile-griff" aria-hidden="true">⠿</span>
      <button type="button" className="invoice-zeile-inhalt" onClick={onBearbeiten}>
        <span className="invoice-zeile-nr">{optional ? "○" : nummer}</span>
        <span className="invoice-zeile-titel">
          <strong>{line.title.trim() || "Ohne Bezeichnung"}</strong>
          {optional && <i>optional</i>}
          {line.description.trim() && <small>{line.description.trim()}</small>}
        </span>
        <span className="invoice-zeile-zahl invoice-zeile-menge">{line.quantity}</span>
        <span className="invoice-zeile-zahl invoice-zeile-einheit">{line.unit_label}</span>
        <span className="invoice-zeile-zahl invoice-zeile-preis">{euro(Number(line.unit_price_net) || 0)}</span>
        <span className="invoice-zeile-zahl invoice-zeile-ust">{line.vat_rate_percent} %</span>
        {/* Auch optionale Zeilen zeigen ihren Betrag — leiser (`ist-optional`),
            aber sichtbar. Vorher stand hier „—"; seit die Optionalsumme unter
            dem Block steht, waere sie damit nicht nachrechenbar, und im PDF
            stand der Betrag ohnehin schon (Benjamin, 15.09.2026). */}
        <span className={"invoice-zeile-zahl invoice-zeile-netto" + (optional ? " ist-optional" : "")}>
          {euro(netto)}
        </span>
      </button>
      <span className="invoice-zeile-werkzeuge">
        <button
          type="button"
          onClick={onToggleOptional}
          title={optional ? "Wieder mitberechnen" : "Als optionale Position führen"}
        >
          {optional ? "∑" : "o"}
        </button>
        <button type="button" onClick={onDuplicate} title="Position duplizieren">⧉</button>
        <button
          type="button"
          onClick={onSaveToCatalog}
          disabled={katalogLaeuft}
          title="In den Positionskatalog übernehmen"
        >
          ▤
        </button>
        <button
          className="invoice-line-remove"
          type="button"
          onClick={onRemove}
          aria-label="Position entfernen"
        >
          ×
        </button>
      </span>
    </div>
  );
}
